import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './SlidingTabs.module.css';

interface TabIcon {
  className: string;
  color?: string;
  size?: string;
}

interface Tab {
  id: string;
  label: string;
  icon?: TabIcon;
}

interface SlidingTabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

interface UnderlineStyle {
  left: number;
  width: number;
}

const SlidingTabs: React.FC<SlidingTabsProps> = ({ tabs, activeTab, onTabChange }) => {
  const [underlineStyle, setUnderlineStyle] = useState<UnderlineStyle>({ left: 0, width: 0 });
  const tabRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});

  tabs.forEach((tab) => {
    if (!tabRefs.current[tab.id]) {
      tabRefs.current[tab.id] = React.createRef<HTMLDivElement>();
    }
  });

  const updateUnderlinePosition = useCallback(() => {
    const activeTabRef = tabRefs.current[activeTab];

    if (activeTabRef?.current) {
      const { offsetLeft, offsetWidth } = activeTabRef.current;
      setUnderlineStyle({
        left: offsetLeft,
        width: offsetWidth,
      });
    }
  }, [activeTab]);

  useEffect(() => {
    updateUnderlinePosition();
  }, [updateUnderlinePosition]);

  return (
    <div className={styles.container}>
      {tabs.map((tab) => (
        <div
          key={tab.id}
          ref={tabRefs.current[tab.id]}
          className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : styles.tabInactive}`}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.label}
          {tab.icon && (
            <i
              className={`${tab.icon.className} ${styles.icon}`}
              style={{
                color: tab.icon.color || '#000',
                fontSize: tab.icon.size || '14px',
              }}
              aria-hidden="true"
            />
          )}
        </div>
      ))}
      <div
        className={styles.underline}
        style={{
          left: `${underlineStyle.left}px`,
          width: `${underlineStyle.width}px`,
        }}
      />
    </div>
  );
};

export default SlidingTabs;
